'use client';

import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Typography, Dialog, Grid, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useVideoAskModal } from '@/components/video-ask/lib/video-ask-client';
import { config } from '@/config/environment';
import { sectionSpacingCss } from '../section-spacing';
import { useGetExperienceByIdLazyQuery } from '@/generated/graphql';
import ExperienceCard from '@/components/cards/experience-card/experience-card';
import { useRouter } from 'next/navigation';

const Container = styled('section')`
  ${sectionSpacingCss}
  display: flex;
  justify-content: center;
  padding-left: clamp(16px, 5vw, 48px);
  padding-right: clamp(16px, 5vw, 48px);
  color: #fff;
`;

const Card = styled('div')`
  padding: 2rem 1.25rem;
  border-radius: 18px;
  max-width: 720px;
  width: 100%;
  text-align: center;
`;

const CTAButton = styled('a')`
  display: inline-block;
  font-family:
    work sans,
    sans-serif;
  background: #ffd02f;
  color: #000;
  font-weight: 520;
  font-size: 20px;
  padding: 12px 28px;
  border-radius: 999px;
  margin-top: 40px;
  text-decoration: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  transition: background-color 160ms ease;
  :hover {
    background-color: #f0926e;
  }
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: rgba(59, 25, 50, 0.55);
    border-radius: 18px;
    padding: 20px;
    max-width: 900px;
    width: 90%;
    position: relative;
  }
  .MuiBackdrop-root {
    backdrop-filter: blur(4px);
    background-color: rgba(0, 0, 0, 0.6);
  }
`;

const ModalContent = styled.div`
  padding: 20px;
  color: white;
`;

const ModalTitle = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 32px;
  text-align: center;
  color: white;
  margin-bottom: 30px;
  @media (max-width: 600px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 16px;
  right: 16px;
  color: white;
  z-index: 1;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ExperienceGrid = styled(Grid)`
  margin-top: 16px;
`;

const LoadingText = styled(Typography)`
  text-align: center;
  color: white;
  padding: 40px;
`;

// Helper function to extract experience ID from URL
const extractExperienceId = (url: string): string | null => {
  if (!url) return null;
  try {
    // Handle URLs like: https://www.curiko.ca/experiences/2b69e0f6-67fa-4402-b1c3-8c6be7edb020/
    const match = url.match(/\/experiences\/([a-f0-9-]+)/i);
    return match ? match[1] : null;
  } catch {
    return null;
  }
};

export default function ConnectWithCoach({ data }: { data?: any }): React.ReactElement {
  const archiveMode = config.platformMode === 'archive';
  const ctaUrl = data?.ctaUrl || '';
  const ctaUrl2 = data?.ctaUrl2 || '';
  const videoAskUrl = data?.videoAskUrl || '';
  const router = useRouter();

  const [modalOpen, setModalOpen] = useState(false);
  const [openVideoAsk] = useVideoAskModal(
    videoAskUrl || ctaUrl, // Use ctaUrl as fallback for the hook
    {},
    { modalType: data?.fullscreen ? 'Fullscreen' : 'Side' },
  );

  // Extract experience IDs from both URLs
  const experienceId1 = ctaUrl ? extractExperienceId(ctaUrl) : null;
  const experienceId2 = ctaUrl2 ? extractExperienceId(ctaUrl2) : null;

  // Use lazy queries to fetch experiences when needed
  const [loadExperience1, { data: exp1Data, loading: loading1 }] = useGetExperienceByIdLazyQuery({
    fetchPolicy: 'cache-first',
  });

  const [loadExperience2, { data: exp2Data, loading: loading2 }] = useGetExperienceByIdLazyQuery({
    fetchPolicy: 'cache-first',
  });

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = e => {
    e.preventDefault(); // Always prevent default navigation

    // If both URLs are present, show the experience selection modal
    if (ctaUrl && ctaUrl2 && experienceId1 && experienceId2) {
      // Load both experiences
      loadExperience1({ variables: { id: experienceId1 } });
      loadExperience2({ variables: { id: experienceId2 } });
      setModalOpen(true);
      return;
    }

    // If there's a separate VideoAsk URL, open the VideoAsk modal
    if (videoAskUrl && videoAskUrl !== ctaUrl) {
      try {
        openVideoAsk();
      } catch (err) {
        // fallback to natural navigation if modal can't open

        if (ctaUrl) {
          router.push(ctaUrl);
        }
      }
      return;
    }

    // Otherwise, navigate to ctaUrl (single URL behavior)

    if (ctaUrl) {
      router.push(ctaUrl);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleExperienceSelect = (url: string) => {
    setModalOpen(false);
    router.push(url);
  };

  const handleKeyDown = (url: string) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleExperienceSelect(url);
    }
  };

  const experience1 = exp1Data?.experience;
  const experience2 = exp2Data?.experience;
  const isLoading = loading1 || loading2;

  return (
    <>
      <Container aria-labelledby="connect-coach-title">
        <Card>
          <Typography
            variant="body1"
            sx={{ font: 'work sans', fontSize: '20px', color: 'rgba(255,255,255,0.9)' }}
          >
            {data?.heading || 'Want to get started or learn more?'} <br />
            {data?.subtext || 'Book a 15 minute chat with a Curiko Coach.'}
          </Typography>

          {!archiveMode && (
            <CTAButton href={ctaUrl || '#book-coach'} role="button" onClick={handleClick}>
              {data?.ctaLabel || 'Connect with a coach'}
            </CTAButton>
          )}
        </Card>
      </Container>

      {/* Experience Selection Modal */}
      <StyledDialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="experience-selection-dialog"
      >
        <CloseButton onClick={handleModalClose} aria-label="Close dialog">
          <CloseIcon />
        </CloseButton>
        <ModalContent>
          <ModalTitle variant="h2" id="experience-selection-dialog">
            Connect with a coach
          </ModalTitle>

          {isLoading ? (
            <LoadingText>Loading experiences...</LoadingText>
          ) : (
            <ExperienceGrid container spacing={3} justifyContent="center">
              {experience1 && (
                <Grid item xs={12} sm={6}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleExperienceSelect(ctaUrl)}
                    onKeyDown={handleKeyDown(ctaUrl)}
                    style={{ cursor: 'pointer' }}
                  >
                    <ExperienceCard experience={experience1 as any} />
                  </div>
                </Grid>
              )}
              {experience2 && (
                <Grid item xs={12} sm={6}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleExperienceSelect(ctaUrl2)}
                    onKeyDown={handleKeyDown(ctaUrl2)}
                    style={{ cursor: 'pointer' }}
                  >
                    <ExperienceCard experience={experience2 as any} />
                  </div>
                </Grid>
              )}
            </ExperienceGrid>
          )}
        </ModalContent>
      </StyledDialog>
    </>
  );
}
